import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Container, Typography, Grid, Card, CardActionArea, CardContent, CardMedia, TextField, FormControlLabel, Radio, RadioGroup, Select, MenuItem, Button, Box, Fab } from '@mui/material';
import { fetchAllCourses } from '../config/firestoreConfig';
import LoadingSpinner from '../components/LoadingSpinner';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';

// Custom hook for handling search and filter logic
const useFilteredCourses = (courses, searchQuery, filter, sortOrder) => {
  return useMemo(() => {
    let filtered = [...courses];

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter((course) =>
        course[filter].toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });

    return filtered;
  }, [courses, searchQuery, filter, sortOrder]);
};

const CourseListingPage = () => {
  const [courses, setCourses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('title');
  const [sortOrder, setSortOrder] = useState('desc');
  const [searchVisible, setSearchVisible] = useState(false); // Manage search section visibility
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md')); // Check if it's tablet

  useEffect(() => {
    const getCourses = async () => {
      setIsLoading(true);
      const coursesData = await fetchAllCourses();
      setCourses(coursesData);
      setIsLoading(false);
    };
    getCourses();
  }, []);

  const filteredCourses = useFilteredCourses(courses, searchQuery, filter, sortOrder);

  // Handlers
  const handleSearchQueryChange = (e) => setSearchQuery(e.target.value);
  const handleFilterChange = (e) => setFilter(e.target.value);
  const handleSortOrderChange = (e) => setSortOrder(e.target.value);
  const handleSearchToggle = () => {
    setSearchVisible((prev) => !prev);
    handleClearFilters();
  }
  const handleClearFilters = () => {
    setSearchQuery('');
    setFilter('title');
    setSortOrder('desc');
  };

  if (isLoading) return <LoadingSpinner />;

  return (
    <Container maxWidth="lg" sx={{ py: isMobile ? 4 : 6, mt: isMobile ? 4 : isTablet ? 6 : 7 , backgroundColor: '#fff' }}>
      {/* Title */}
      <Typography variant={isMobile ? 'h5' : 'h4'} component="h1" sx={{ fontWeight: 'bold', mb: 3, color: '#000' }}>
        Available Courses
      </Typography>

      {/* Floating Search Button */}
      <Fab
        color="primary"
        aria-label="search"
        onClick={handleSearchToggle}
        sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 999, backgroundColor: '#000', '&:hover': { backgroundColor: '#fff', color: '#000', border: '1.2px solid #000', } }}
      >
        <SearchIcon />
      </Fab>

      {/* Conditional Search Section */}
      {searchVisible && (
        <Box sx={{ position: 'relative', width: '100%', border: '2px solid #000', borderRadius: 3, padding: 2, backgroundColor: '#f9f9f9', boxSizing: 'border-box', mb: 2 }}>
          <TextField
            label="Search Courses"
            variant="outlined"
            value={searchQuery}
            onChange={handleSearchQueryChange}
            fullWidth
            autoFocus
            sx={{
              mb: 2, 
              backgroundColor: '#fff',
              borderRadius: 2,
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: '#000',
                },
                '&:hover fieldset': {
                  borderColor: '#000',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#000',  
                },
              },
              '& .MuiInputLabel-root': {
                color: '#000',  
              },
            }}
          />

          {/* Filter and Sort Section */}
          <Grid container spacing={3} sx={{ mb: 2, alignItems: 'center' }}>
            {/* Filter Options */}
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Filter By</Typography>
              <RadioGroup value={filter} sx={{padding: 1}} onChange={handleFilterChange} row>
                <FormControlLabel value="title" control={<Radio sx={{ color: '#000', padding: 0, '&.Mui-checked': { color: '#000' } }}/>} label="By Title" />
                <FormControlLabel value="description" control={<Radio sx={{ color: '#000', padding: 0, '&.Mui-checked': { color: '#000' } }}/>} label="By Description" />
                <FormControlLabel value="id" control={<Radio sx={{ color: '#000', padding: 0, '&.Mui-checked': { color: '#000' } }} />} label="By Course Code" />
              </RadioGroup>
            </Grid>

            {/* Sort Options */}
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Sort By Date</Typography>
              <Select value={sortOrder} onChange={handleSortOrderChange} fullWidth>
                <MenuItem value="asc">Ascending</MenuItem>
                <MenuItem value="desc">Descending</MenuItem>
              </Select>
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Grid container spacing={2} sx={{ justifyContent: 'space-between' }}>
            <Grid item>
              <Button variant="contained"
                sx={{
                  textTransform: 'none',
                  fontWeight: 'bold',
                  backgroundColor: '#fff', 
                  color: '#000',
                  border: '1px solid #000',
                  '&:hover': {
                    backgroundColor: '#000', 
                    color: '#fff', 
                  },
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }} 
                onClick={handleClearFilters}>
                  Clear Filters
              </Button>
            </Grid>
            <Grid item>
              <Button variant="contained"
                sx={{
                  textTransform: 'none',
                  fontWeight: 'bold',
                  backgroundColor: '#000', 
                  color: '#fff', 
                  '&:hover': {
                    backgroundColor: '#fff', 
                    color: '#000', 
                    border: '1px solid #000', 
                  },
                  display: 'flex',
                  alignItems: 'center', 
                  gap: 1, 
                }}
                onClick={handleSearchToggle}>
                  Close Search
              </Button>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Course Cards */}
      <Grid container spacing={4}>
        {filteredCourses.map((course) => (
          <Grid item key={course.id} xs={12} sm={6} md={4}>
            <Card sx={{ boxShadow: 2, borderRadius: 1, transition: 'all 0.3s ease', '&:hover': { boxShadow: 4, transform: 'scale(1.03)' } }}>
              <CardActionArea component={Link} to={`/courses/${course.id}`}>
                {/* <CardMedia component="img" height="180" image={course.image || 'logo.jpeg'} alt={course.title} /> */}
                <CardContent>
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{course.title}</Typography>
                  <Typography variant="body2" sx={{ color: '#000', lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical' }}>
                    {course.description}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

export default CourseListingPage;



